import { useState, useEffect, useRef, useMemo } from 'react';
import { useDB } from '../../hooks/useDB';

// ─── Utilitaires ──────────────────────────────────────────────────────────────
const today      = () => new Date().toISOString().slice(0,10);
const diffJours  = (dateStr) => {
  if (!dateStr) return 9999;
  return Math.floor((new Date(dateStr) - new Date()) / (1000*60*60*24));
};
const formatDate = (d) => d ? new Date(d+'T12:00:00').toLocaleDateString('fr-FR',{day:'2-digit',month:'short',year:'numeric'}) : '—';

// ─── Calcul des notifications actives ────────────────────────────────────────

// ─── Hook notifications push navigateur ──────────────────────────────────────
// Demande la permission et envoie des notifications système quand l'app
// est en arrière-plan ou que l'onglet n'est pas actif
export function usePushNotifications() {
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );

  const requestPermission = async () => {
    if (typeof Notification === 'undefined') return 'denied';
    const result = await Notification.requestPermission();
    setPermission(result);
    return result;
  };

  const sendPush = (titre, options = {}) => {
    if (permission !== 'granted') return;
    // Ne pas envoyer si l'onglet est actif et visible
    if (document.visibilityState === 'visible') return;
    try {
      const n = new Notification(titre, {
        icon:   '/logo192.png',
        badge:  '/logo192.png',
        lang:   'fr',
        ...options,
      });
      // Fermer automatiquement après 8 secondes
      setTimeout(() => n.close(), 8000);
      // Clic → focus sur la fenêtre
      n.onclick = () => { window.focus(); n.close(); };
    } catch {}
  };

  return { permission, requestPermission, sendPush };
}

export function useNotifications({ user, staff, conges, docs, medecine, entretiens,
  formations, benevoles, onboarding, messages, annonces, contratsGen, rup, espaceSections,
  alertesConfig }) {


  return useMemo(() => {
    if (!user) return [];

    // ── Résolution de la config alertes ──────────────────────────────────────
    // alertesConfig peut être un tableau plat ou un objet { _flat, systeme:{id:{active}} }
    const alertesFlat = alertesConfig?._flat ?? (Array.isArray(alertesConfig) ? alertesConfig : null);
    // isActive(id) : true si l'alerte est active ou si la config est absente (défaut = actif)
    const isActive = (id) => {
      if (!alertesFlat) return true;
      const found = alertesFlat.find(a => a.id === id);
      return found ? found.active !== false : true;
    };
    const isRH      = user.roles?.includes('rh') || user.roles?.includes('admin');
    const isManager = user.roles?.includes('manager');
    const myStaffId = user.staffId;
    const notifs    = [];
    const add = (id, type, icon, titre, detail, module, page, urgence='normal') => {
      notifs.push({ id, type, icon, titre, detail, module, page, urgence, date: today() });
    };

    // ── CONGÉS ────────────────────────────────────────────────────────────────
    if (isRH || isManager) {
      // Statuts RH actionnables : congés sans manager (→ direct RH) ou validés par tous les managers
      const enAttenteRH = isRH ? (conges||[]).filter(c => {
        const nbM = (c.managers||[]).length;
        if (['Approuvé RH','Approuve RH','APPROUVÉ','Refusé RH','Refuse RH'].includes(c.statut)) return false;
        if (nbM === 0) return c.statut === 'En attente RH';
        if (nbM === 1) return c.statut === 'Valide M1';
        return c.statut === 'Valide M2';
      }) : [];

      enAttenteRH.forEach(c => {
        add(`conge_rh_${c.id}`,'conge','🌴',
          `Congés à valider (RH)`,
          `${c.nomSalarie||c.staffNom||'—'} · ${formatDate(c.dateDebut)} → ${formatDate(c.dateFin)}`,
          'Congés','conges','high');
      });

      // Statuts Manager actionnables : en attente du rang de ce manager
      if (isManager) {
        const enAttenteMgr = (conges||[]).filter(c => {
          const idx = (c.managers||[]).findIndex(m => String(m.staffId) === String(myStaffId));
          if (idx < 0) return false;
          return c.statut === `En attente M${idx + 1}`;
        });
        enAttenteMgr.forEach(c => {
          add(`conge_mgr_${c.id}`,'conge','🌴',
            `Congés à valider`,
            `${c.nomSalarie||c.staffNom||'—'} · ${formatDate(c.dateDebut)} → ${formatDate(c.dateFin)}`,
            'Congés','conges','high');
        });
      }
    }

    // ── DOCUMENTS EXPIRÉS / À RENOUVELER ────────────────────────────────────
    if (isRH) {
      if (isActive('carte_pro_expiration')) {
        (docs||[]).forEach(d => {
          const j = diffJours(d.dateExpiration || d.dateEcheance);
          if (j !== 9999 && j <= 60) {
            const urgence = j < 0 ? 'urgent' : j <= 14 ? 'high' : 'normal';
            const label   = j < 0 ? `Expiré depuis ${Math.abs(j)}j` : `Expire dans ${j}j`;
            add(`doc_exp_${d.id}`,'doc','📄',
              `Document expirant — ${d.type||d.categorie||'Document'}`,
              `${d.nomSalarie||'—'} · ${d.titre||d.type||'—'} · ${label}`,
              'Documents','docs', urgence);
          }
        });
      }

      // Visites médicales (stagiaires et service civique exclus — non soumis)
      if (isActive('visite_medicale_expiration')) {
        const TYPES_NON_SOUMIS = ['Stagiaire', 'Service civique'];
        (medecine||[]).forEach(m => {
          const sal = (staff||[]).find(s => String(s.id) === String(m.staffId));
          if (sal && TYPES_NON_SOUMIS.includes(sal.type)) return;
          const j = diffJours(m.prochaineVisite || m.dateProchaine);
          if (j !== 9999 && j <= 60) {
            const urgence = j < 0 ? 'urgent' : j <= 14 ? 'high' : 'normal';
            const label   = j < 0 ? `En retard de ${Math.abs(j)}j` : `Dans ${j}j`;
            add(`med_${m.id}`,'medecine','🏥',
              `Visite médicale — ${m.type||'Renouvellement'}`,
              `${m.nomSalarie||'—'} · ${label}`,
              'Médecine','medecine', urgence);
          }
        });
      }

      // Entretiens EP en retard (> 20 mois)
      if (isActive('entretien_annuel_rappel')) {
        const staffActifs = (staff||[]).filter(s => s.statut === 'Actif' || !s.statut);
        staffActifs.forEach(s => {
          const eps = (entretiens||[])
            .filter(e => String(e.staffId) === String(s.id) && e.type === 'EP' && e.statut === 'finalise')
            .sort((a,b) => b.dateEntretien > a.dateEntretien ? 1 : -1);
          const dernier = eps[0];
          const mois = dernier
            ? Math.floor((new Date() - new Date(dernier.dateEntretien)) / (1000*60*60*24*30))
            : 999;
          if (mois >= 20) {
            const urgence = mois >= 24 ? 'urgent' : 'high';
            add(`ep_retard_${s.id}`,'entretien','💬',
              `EP ${mois >= 24 ? 'EN RETARD' : 'bientôt dû'} — ${s.prenom} ${s.nom}`,
              `${dernier ? `Dernier EP : ${formatDate(dernier.dateEntretien)} (${mois} mois)` : 'Aucun EP enregistré'} · Obligation : 24 mois`,
              'Entretiens','entretiens', urgence);
          }
        });
      }

      // Formations expirées
      if (isActive('certification_expiration')) {
        (formations||[]).forEach(f => {
          const j = diffJours(f.dateExpiration || f.dateEcheance);
          if (j !== 9999 && j <= 90) {
            const urgence = j < 0 ? 'urgent' : j <= 30 ? 'high' : 'normal';
            const label   = j < 0 ? `Expirée depuis ${Math.abs(j)}j` : `Expire dans ${j}j`;
            add(`form_exp_${f.id}`,'formation','🏆',
              `Certification expirante`,
              `${f.nomSalarie||'—'} · ${f.titre||f.formation||'—'} · ${label}`,
              'Formations','formations', urgence);
          }
        });
      }

      // Bénévoles assurance expirée
      if (isActive('assurance_benevole')) {
        (benevoles||[]).forEach(b => {
          const j = diffJours(b.assuranceExpiration || b.dateAssurance);
          if (j !== 9999 && j <= 90) {
            const urgence = j < 0 ? 'urgent' : j <= 30 ? 'high' : 'normal';
            const label   = j < 0 ? `Expirée depuis ${Math.abs(j)}j` : `Expire dans ${j}j`;
            add(`ben_ass_${b.id}`,'benevole','🤝',
              `Assurance bénévole expirante`,
              `${b.prenom||''} ${b.nom||''} · ${label}`,
              'Bénévoles','benevoles', urgence);
          }
        });
      }

      // Contrats CDD fin imminente
      if (isActive('contrat_fin_cdd')) {
        (contratsGen||[]).filter(c => c.typContrat === 'CDD' && c.dateFin).forEach(c => {
          const j = diffJours(c.dateFin);
          if (j <= 30) {
            const urgence = j < 0 ? 'urgent' : j <= 7 ? 'high' : 'normal';
            const label   = j < 0 ? `Terminé depuis ${Math.abs(j)}j` : `Fin dans ${j}j`;
            add(`cdd_fin_${c.id}`,'contrat','📜',
              `Fin de CDD imminente`,
              `${c.nomSalarie||'—'} · ${label}`,
              'Contrats','contrats', urgence);
          }
        });
      }

      // Onboarding incomplet > 7 jours
      if (isActive('onboarding_etape_manquante')) {
        (onboarding||[]).forEach(o => {
          const sal = (staff||[]).find(s => String(s.id) === String(o.staffId));
          if (!sal) return;
          const embauche = sal.dateEmbauche;
          if (!embauche) return;
          const joursDepuis = Math.floor((new Date() - new Date(embauche)) / (1000*60*60*24));
          const etapes = Array.isArray(o) ? o : (o.etapes || []);
          const incomplet = etapes.filter(i => !i.done).length;
          if (joursDepuis >= 7 && incomplet > 0) {
            add(`onb_inc_${o.staffId}`,'onboarding','🔑',
              `Onboarding incomplet`,
              `${sal.prenom} ${sal.nom} · ${incomplet} étape(s) manquante(s) · ${joursDepuis}j depuis l'embauche`,
              'Onboarding','onboarding','high');
          }
        });
      }

      // RUP — titres de séjour expirants
      if (isActive('titre_sejour_expiration')) {
        (rup||[]).forEach(r => {
          const j = diffJours(r.titreSejour_fin || r.finTitreSejour);
          if (j !== 9999 && j <= 60) {
            const urgence = j < 0 ? 'urgent' : j <= 14 ? 'high' : 'normal';
            const label   = j < 0 ? `Expiré depuis ${Math.abs(j)}j` : `Expire dans ${j}j`;
            add(`rup_titre_${r.id}`,'rup','📒',
              `Titre de séjour expirant`,
              `${r.prenom||''} ${r.nom||''} · ${label}`,
              'RUP','rup', urgence);
          }
        });
      }
    }

    // ── MESSAGES NON LUS ────────────────────────────────────────────────────
    const unreadMsg = (messages||[]).filter(m =>
      m.destinataireId === user.id && !m.lu && !m.archive
    );
    if (unreadMsg.length > 0) {
      add('msgs_non_lus','message','💬',
        `${unreadMsg.length} message(s) non lu(s)`,
        unreadMsg.slice(0,2).map(m => m.expediteurNom || '—').join(', ') + (unreadMsg.length > 2 ? '…' : ''),
        'Messagerie','communication','high');
    }

    // ── ANNONCES NON LUES ───────────────────────────────────────────────────
    const mySection = (staff||[]).find(s => String(s.id) === String(myStaffId))?.section || '';
    const unreadAnn = (annonces||[]).filter(a =>
      !a.archive && !a.lu?.includes(user.id) &&
      (a.cibles?.includes('tous') ||
       (a.cibles?.includes('section') && a.sections?.includes(mySection)))
    );
    if (unreadAnn.length > 0) {
      add('anns_non_lues','annonce','📢',
        `${unreadAnn.length} annonce(s) non lue(s)`,
        unreadAnn[0]?.titre || '—',
        'Communication','communication','normal');
    }

    // ── Annonces sections ────────────────────────────────────────────────────
    if (espaceSections) {
      const myStaffSec  = (staff||[]).find(s => String(s.id) === String(user?.staffId||''));
      const mySectionSec = myStaffSec?.section || '';
      const espSec    = espaceSections[mySectionSec];
      const msgs      = espSec?.messages || [];
      // Notifier les 3 dernières annonces des dernières 48h
      const limit48   = new Date(Date.now() - 48*3600*1000).toISOString();
      msgs.filter(m => m.date && m.date > limit48 &&
        m.auteur !== `${user?.prenom||''} ${user?.nom||''}`.trim()
      ).slice(0, 3).forEach(m => {
        add(`sec_${m.id}`, 'section', '📣',
          `Annonce — ${mySection}`,
          m.contenu.slice(0, 80) + (m.contenu.length > 80 ? '…' : ''),
          'Sections', 'sections', 'normal'
        );
      });
    }

    // ── Trier : urgent > high > normal, puis par date ────────────────────────
    const ordre = { urgent: 0, high: 1, normal: 2 };
    // ── DOCUMENTS DÉPOSÉS PAR LES SALARIÉS ────────────────────────────────────
    if (isRH) {
      const depots = (docs||[]).filter(d => d.depotSalarie && d.statut === 'En attente');
      depots.forEach(d => {
        const sal = (staff||[]).find(s => s.id === d.staffId);
        add(`depot_doc_${d.id}`, 'doc', '📤',
          'Document déposé par un salarié',
          `${sal ? sal.prenom+' '+sal.nom : 'Salarié'} · ${d.type} · ${formatDate(d.dateDepot)}`,
          'Documents', 'docs', 'high');
      });
    }

    notifs.sort((a,b) => (ordre[a.urgence]||2) - (ordre[b.urgence]||2));


    return notifs;
  }, [user, staff, conges, docs, medecine, entretiens, formations,
      benevoles, onboarding, messages, annonces, contratsGen, rup, espaceSections, alertesConfig]);
}

// ─── Cloche topbar ────────────────────────────────────────────────────────────
export function ClocheNotifications({ notifs, onNavigate, user }) {
  const { permission, requestPermission, sendPush } = usePushNotifications();
  const [open, setOpen]     = useState(false);
  const [filtre, setFiltre] = useState('tout');

  // Notifications lues — stockées par utilisateur dans Supabase
  // clé unique par user → synchronisée sur tous ses appareils
  const notifKey = user?.id ? `cmob_notifs_lues_${user.id}` : 'cmob_notifs_lues_anon';
  const [luesArr, setLuesArr] = useDB(notifKey, []);
  const lues = new Set(Array.isArray(luesArr) ? luesArr : []);
  const panelRef              = useRef(null);

  // Fermer en cliquant dehors
  useEffect(() => {
    const handler = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const marquerLue = (id) => {
    const next = [...new Set([...(luesArr||[]), id])];
    setLuesArr(next);
  };
  const toutMarquerLu = () => {
    const next = notifs.map(n => n.id);
    setLuesArr(next);
  };

  const nonLues    = notifs.filter(n => !lues.has(n.id));

  // Envoyer push pour les nouvelles notifs urgentes non lues
  const prevNonLuesRef = useRef(new Set());
  useEffect(() => {
    const urgentesNonLues = nonLues.filter(n => n.urgence === 'urgent');
    urgentesNonLues.forEach(n => {
      if (!prevNonLuesRef.current.has(n.id)) {
        sendPush(`🔔 ${n.titre}`, { body: n.detail || '', tag: n.id });
      }
    });
    prevNonLuesRef.current = new Set(urgentesNonLues.map(n => n.id));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonLues]);
  const urgentes   = notifs.filter(n => n.urgence === 'urgent');
  const affichees  = filtre === 'non_lues' ? nonLues
                   : filtre === 'urgentes' ? urgentes
                   : notifs;

  const URGENCE_STYLE = {
    urgent: { bg:'rgba(220,38,38,0.08)',  border:'rgba(220,38,38,0.25)',  dot:'#dc2626', label:'🔴 Urgent'  },
    high:   { bg:'rgba(180,83,9,0.08)',   border:'rgba(180,83,9,0.25)',   dot:'#b45309', label:'🟡 Important'},
    normal: { bg:'rgba(0,48,135,0.05)',   border:'rgba(0,48,135,0.15)',   dot:'#6b8bad', label:'🔵 Info'     },
  };

  return (
    <div style={{ position:'relative' }} ref={panelRef}>
      {/* ── Bouton cloche ── */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          position:'relative', width:36, height:36, borderRadius:'50%', cursor:'pointer',
          background: nonLues.length > 0
            ? (urgentes.length > 0 ? 'rgba(220,38,38,0.12)' : 'rgba(180,83,9,0.10)')
            : 'var(--bg3)',
          border: nonLues.length > 0
            ? `1.5px solid ${urgentes.length > 0 ? '#dc2626' : '#b45309'}`
            : '1.5px solid var(--border)',
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:'1.1rem', transition:'all 0.2s',
        }}
      >
        🔔
        {nonLues.length > 0 && (
          <span style={{
            position:'absolute', top:-5, right:-5,
            background: urgentes.length > 0 ? '#dc2626' : '#b45309',
            color:'#fff', borderRadius:'50%',
            minWidth:18, height:18, padding:'0 4px',
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:'0.58rem', fontWeight:800, lineHeight:1,
          }}>
            {nonLues.length > 99 ? '99+' : nonLues.length}
          </span>
        )}
      </button>

      {/* ── Panneau ── */}
      {open && (
        <div style={{
          position:'fixed', top:60, right:16, width:'min(380px, calc(100vw - 32px))', maxHeight:'80vh',
          background:'var(--card)', borderRadius:14, boxShadow:'0 8px 40px rgba(0,0,0,0.18)',
          border:'1px solid var(--border)', zIndex:9999, display:'flex', flexDirection:'column',
          overflow:'hidden',
        }}>
          {/* En-tête */}
          <div style={{ padding:'14px 16px', borderBottom:'1px solid var(--border)',
            background:'linear-gradient(135deg,var(--bleu),#0055aa)', color:'#fff' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div style={{ fontWeight:800, fontSize:'0.95rem' }}>
                🔔 Notifications
                {nonLues.length > 0 && (
                  <span style={{ marginLeft:8, background:'rgba(255,255,255,0.2)',
                    padding:'1px 8px', borderRadius:10, fontSize:'0.75rem' }}>
                    {nonLues.length} non lue(s)
                  </span>
                )}
              </div>
              {nonLues.length > 0 && (
                <button onClick={toutMarquerLu} style={{ background:'rgba(255,255,255,0.15)',
                  border:'none', color:'#fff', fontSize:'0.72rem', padding:'3px 10px',
                  borderRadius:8, cursor:'pointer', fontWeight:700 }}>
                  ✓ Tout marquer lu
                </button>
              )}
            </div>
            {/* Filtres */}
            <div style={{ display:'flex', gap:6, marginTop:10 }}>
              {[
                { key:'tout',      label:`Tout (${notifs.length})` },
                { key:'non_lues',  label:`Non lues (${nonLues.length})` },
                { key:'urgentes',  label:`🔴 Urgentes (${urgentes.length})` },
              ].map(f => (
                <button key={f.key} onClick={() => setFiltre(f.key)} style={{
                  padding:'3px 10px', borderRadius:8, fontSize:'0.72rem', fontWeight:700,
                  cursor:'pointer', border:'none', transition:'all 0.15s',
                  background: filtre===f.key ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)',
                  color:'#fff',
                }}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Liste */}
          <div style={{ flex:1, overflowY:'auto' }}>
            {affichees.length === 0 ? (
              <div style={{ padding:32, textAlign:'center', color:'var(--muted)' }}>
                <div style={{ fontSize:'2rem', marginBottom:8 }}>✅</div>
                <div style={{ fontWeight:600 }}>Tout est à jour !</div>
                <div style={{ fontSize:'0.78rem', marginTop:4 }}>Aucune notification{filtre !== 'tout' ? ' dans ce filtre' : ''}</div>
              </div>
            ) : affichees.map(n => {
              const isLue = lues.has(n.id);
              const s = URGENCE_STYLE[n.urgence] || URGENCE_STYLE.normal;
              return (
                <div key={n.id}
                  onClick={() => { marquerLue(n.id); onNavigate(n.page); setOpen(false); }}
                  style={{
                    padding:'11px 14px', cursor:'pointer', borderBottom:'1px solid var(--border)',
                    background: isLue ? 'var(--bg)' : s.bg,
                    borderLeft: `3px solid ${isLue ? 'transparent' : s.dot}`,
                    opacity: isLue ? 0.65 : 1,
                    transition:'background 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg2)'}
                  onMouseLeave={e => e.currentTarget.style.background = isLue ? 'var(--bg)' : s.bg}
                >
                  <div style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
                    <span style={{ fontSize:'1.1rem', flexShrink:0, marginTop:1 }}>{n.icon}</span>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:6 }}>
                        <div style={{ fontWeight: isLue ? 500 : 700, fontSize:'0.83rem',
                          color:'var(--text)', lineHeight:1.3 }}>
                          {n.titre}
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:4, flexShrink:0 }}>
                          <span style={{ fontSize:'0.62rem', fontWeight:700,
                            color: s.dot, whiteSpace:'nowrap' }}>
                            {n.module}
                          </span>
                          {!isLue && (
                            <button onClick={e => { e.stopPropagation(); marquerLue(n.id); }}
                              style={{ background:'none', border:'none', cursor:'pointer',
                                color:'var(--muted)', fontSize:'0.7rem', padding:0 }}
                              title="Marquer comme lu">✓</button>
                          )}
                        </div>
                      </div>
                      <div style={{ fontSize:'0.76rem', color:'var(--muted)', marginTop:3,
                        whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                        {n.detail}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pied */}
          {/* Bouton activation notifications push */}
      {permission !== 'granted' && (
        <div style={{
          padding: '10px 16px', background: 'rgba(0,48,135,0.05)',
          borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: '0.78rem', color: 'var(--muted)', flex: 1 }}>
            🔔 Recevoir des alertes même si l'onglet est inactif
          </span>
          <button
            onClick={requestPermission}
            style={{
              padding: '4px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
              background: 'var(--bleu)', color: '#fff',
              fontWeight: 700, fontSize: '0.75rem', fontFamily: 'inherit', flexShrink: 0,
            }}
          >
            Activer
          </button>
        </div>
      )}
      {notifs.length > 0 && (
            <div style={{ padding:'10px 14px', borderTop:'1px solid var(--border)',
              background:'var(--bg2)', fontSize:'0.72rem', color:'var(--muted)',
              display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span>{notifs.length} alerte(s) au total</span>
              <button onClick={() => { onNavigate('alertes-config'); setOpen(false); }}
                style={{ background:'none', border:'none', cursor:'pointer',
                  color:'var(--bleu)', fontWeight:700, fontSize:'0.72rem' }}>
                ⚙️ Gérer les alertes
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
